import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Search,
  Download,
  LogOut,
  Users,
  MapPin,
  ExternalLink,
  RefreshCw,
  UserCircle,
  Building,
  ArrowLeft,
  Eye,
  CheckCircle,
  Settings
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const ChaptersPage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [chapters, setChapters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRegions, setIsLoadingRegions] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentRegionName, setCurrentRegionName] = useState("");
  const [extractedChapterIds, setExtractedChapterIds] = useState([]);

  const handleViewChapterDetail = (chapterUrl) => {
    navigate(`/chapter/detail?url=${encodeURIComponent(chapterUrl)}`);
  };

  // Load regions and extracted chapters on mount
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const response = await axios.get(`${API}/regions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRegions(response.data);
      } catch (error) {
        console.error("Error loading regions:", error);
        toast.error("Errore nel caricamento delle regioni");
      } finally {
        setIsLoadingRegions(false);
      }
    };
    
    const loadExtractedChapters = async () => {
      try {
        const response = await axios.get(`${API}/chapters/extracted`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setExtractedChapterIds(response.data.extracted_chapter_ids || []);
      } catch (error) {
        console.error("Error loading extracted chapters:", error);
      }
    };
    
    loadRegions();
    loadExtractedChapters();
  }, [token]);

  // Helper to check if chapter is extracted
  const isChapterExtracted = (chapterUrl) => {
    // Extract chapter ID from URL - handle both encoded and decoded versions
    const match = chapterUrl.match(/chapterId=([^&]+)/);
    if (match) {
      const chapterIdRaw = match[1];
      // Try multiple variations: raw, decoded, double-decoded
      const variations = [
        chapterIdRaw,
        decodeURIComponent(chapterIdRaw),
        decodeURIComponent(decodeURIComponent(chapterIdRaw))
      ];
      
      return variations.some(variation => 
        extractedChapterIds.some(id => 
          id === variation || 
          decodeURIComponent(id) === variation ||
          id === decodeURIComponent(variation)
        )
      );
    }
    return false;
  };

  const handleSearch = async () => {
    if (!selectedRegion) {
      toast.warning("Seleziona una regione");
      return;
    }

    setIsLoading(true);
    setHasSearched(true);

    try {
      const response = await axios.post(
        `${API}/chapters`,
        { region_id: selectedRegion },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setChapters(response.data.chapters);
      setCurrentRegionName(response.data.region_name);
      
      if (response.data.chapters.length === 0) {
        toast.info("Nessun capitolo trovato per questa regione");
      } else {
        toast.success(`Trovati ${response.data.chapters.length} capitoli`);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Errore durante la ricerca dei capitoli");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCSV = async () => {
    if (chapters.length === 0) {
      toast.warning("Nessun dato da esportare");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/chapters/export`,
        chapters,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          responseType: "blob"
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `bni_capitoli_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("File CSV scaricato");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Errore durante l'esportazione");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="chapters-page">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b glass-header">
        <div className="container-app flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <Building className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">BNI Capitoli</h1>
              <p className="text-xs text-muted-foreground hidden sm:block">Ricerca Capitoli Italia</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Link to="/settings">
              <Button variant="outline" size="sm" className="btn-animate" data-testid="settings-button">
                <Settings className="h-4 w-4 mr-2" />
                Impostazioni
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" size="sm" className="btn-animate" data-testid="back-to-dashboard">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
              <UserCircle className="h-4 w-4" />
              <span>{user?.name}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="btn-animate"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </header>

      {/* Filter Bar */}
      <div className="filter-bar py-6">
        <div className="container-app">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              {/* Region Select */}
              <div className="flex-1 space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Seleziona Regione BNI
                </Label>
                <Select 
                  value={selectedRegion} 
                  onValueChange={setSelectedRegion}
                  disabled={isLoadingRegions}
                >
                  <SelectTrigger className="h-11" data-testid="region-select">
                    <SelectValue placeholder={isLoadingRegions ? "Caricamento..." : "Seleziona una regione"} />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        {region.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Search Button */}
              <Button
                onClick={handleSearch}
                disabled={isLoading || !selectedRegion}
                className="h-11 px-6 btn-animate"
                data-testid="search-chapters-button"
              >
                {isLoading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Estrai Capitoli
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container-app py-8">
        {/* Results Header */}
        {hasSearched && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4"
          >
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Capitoli - {currentRegionName}
              </h2>
              <p className="text-sm text-muted-foreground">
                {chapters.length} {chapters.length === 1 ? "capitolo trovato" : "capitoli trovati"}
              </p>
            </div>

            {chapters.length > 0 && (
              <Button
                variant="outline"
                onClick={handleExportCSV}
                className="btn-animate"
                data-testid="export-chapters-csv-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Esporta CSV
              </Button>
            )}
          </motion.div>
        )}

        {/* Results */}
        {!hasSearched ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="empty-state"
          >
            <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center mb-6">
              <Building className="h-10 w-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Estrai Capitoli BNI
            </h3>
            <p className="text-muted-foreground max-w-md">
              Seleziona una regione BNI per estrarre l'elenco dei capitoli con i relativi link alle pagine di dettaglio.
            </p>
          </motion.div>
        ) : chapters.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="empty-state"
          >
            <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center mb-6">
              <Building className="h-10 w-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Nessun Capitolo Trovato
            </h3>
            <p className="text-muted-foreground max-w-md">
              Non sono stati trovati capitoli per questa regione.
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {chapters.map((chapter, index) => {
              const extracted = isChapterExtracted(chapter.url);
              return (
              <motion.div
                key={chapter.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, duration: 0.3 }}
                data-testid={`chapter-card-${index}`}
              >
                <Card className={`member-card h-full overflow-hidden border-border/60 bg-card cursor-pointer hover:border-primary/50 transition-colors ${extracted ? 'ring-2 ring-green-500/30 bg-green-50/30' : ''}`}
                      onClick={() => handleViewChapterDetail(chapter.url)}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-foreground text-base leading-tight">
                            {chapter.name}
                          </h3>
                          {extracted && (
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" title="Dati estratti" />
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-2 text-sm text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{chapter.region}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          className="btn-animate flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewChapterDetail(chapter.url);
                          }}
                          data-testid={`chapter-view-members-${index}`}
                        >
                          <Users className="h-4 w-4 mr-1" />
                          Membri
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="btn-animate flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(chapter.url, '_blank');
                          }}
                          data-testid={`chapter-link-${index}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between">
                      <span className="text-xs text-muted-foreground truncate">
                        {extracted ? 'Dati membri estratti' : 'Clicca per vedere i membri'}
                      </span>
                      {extracted ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )})}
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default ChaptersPage;
